import { Box, IconButton, Slider, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import React, { useState, useEffect } from 'react';
import { ContentArea, WebsiteMockup, HeaderBar, PreviewContainer, PreviewCard } from 'styles';

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const LivePreview = ({ cookieContentState, selectedDomain, defaultLanguage }) => {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const handleKeyboard = (e) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey) {
        if (e.key === '=' || e.key === '+') {
          e.preventDefault();
          setScale((prev) => Math.min(prev + 0.1, 2));
        } else if (e.key === '-') {
          e.preventDefault();
          setScale((prev) => Math.max(prev - 0.1, 0.5));
        } else if (e.key === '0') {
          e.preventDefault();
          setScale(1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, []);

  const handleZoomIn = () => setScale((prev) => clamp(Number((prev + 0.1).toFixed(2)), 0.5, 2));
  const handleZoomOut = () => setScale((prev) => clamp(Number((prev - 0.1).toFixed(2)), 0.5, 2));
  const handleReset = () => setScale(1);

  return (
    <PreviewCard>
      <MDBox p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <MDTypography variant="h5" fontWeight="medium" color="info">
              Canlı Önizleme {selectedDomain && `- ${selectedDomain}`}
            </MDTypography>
            {defaultLanguage && (
              <MDTypography variant="body2" color="text" sx={{ mt: 0.5 }}>
                Varsayılan Dil: {defaultLanguage === "en" ? "İngilizce" : "Türkçe"}
              </MDTypography>
            )}
          </Box>

          {/* Zoom Controls */}
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Uzaklaştır (Ctrl/⌘ + -)">
              <span>
                <IconButton size="small" onClick={handleZoomOut} disabled={scale <= 0.5}>
                  <ZoomOutIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Slider
              value={scale}
              onChange={(_, value) => setScale(clamp(Number(value), 0.5, 2))}
              min={0.5}
              max={2}
              step={0.1}
              sx={{ width: 100 }}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
            />

            <Tooltip title="Yakınlaştır (Ctrl/⌘ + +)">
              <span>
                <IconButton size="small" onClick={handleZoomIn} disabled={scale >= 2}>
                  <ZoomInIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Sıfırla (Ctrl/⌘ + 0)">
              <IconButton size="small" onClick={handleReset}>
                <RestartAltIcon />
              </IconButton>
            </Tooltip>

            <MDTypography variant="caption" color="text" sx={{ minWidth: 45, textAlign: 'right' }}>
              {Math.round(scale * 100)}%
            </MDTypography>
          </Box>
        </Box>

        {/* Scrollable Container */}
        <PreviewContainer
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0)',
            height: '75vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
            },
          }}
        >
          {/* Main wrapper */}
          <Box
            sx={{
              position: 'relative',
              minHeight: '100%',
              width: '100%',
            }}
          >
            {/* WebsiteMockup with scale */}
            <Box
              sx={{
                mx: 'auto',
                minHeight: '100%',
              }}
            >
              <WebsiteMockup>
                <HeaderBar />
                <ContentArea>Website İçerik Alanı</ContentArea>
              </WebsiteMockup>
            </Box>

            {/* Banner overlay wrapper - applies scale without affecting banner's positioning */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
                transform: `scale(${scale})`,
                transformOrigin: 'center center',
                transition: 'transform 0.2s ease',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  '& > *': {
                    pointerEvents: 'auto',
                  },
                }}
              >
                {cookieContentState?.banner}
              </Box>
            </Box>
          </Box>
        </PreviewContainer>
      </MDBox>
    </PreviewCard>
  );
};

export default LivePreview;